import { useState, useEffect } from "react";
import { Tabs, Tab, styled, CircularProgress } from "@mui/material";
import HeaderComponent from "@components/common/Header/Header";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate, useLocation } from "react-router-dom";
import MasterDataTable, { MasterType } from "./components/MasterDataTable";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { EndpointType } from "@/constants/apiEndPoints";

const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

const tabs: Array<{ label: string; path: string; type: MasterType }> = [
  { label: "Commodity", path: "/general-masters/commodity", type: "commodity" },
  {
    label: "Profession",
    path: "/general-masters/profession",
    type: "profession",
  },
  {
    label: "Debtor Type",
    path: "/general-masters/debtor-type",
    type: "debtorType",
  },
  {
    label: "Payment Terms",
    path: "/general-masters/payment-terms",
    type: "paymentTerms",
  },
  {
    label: "Client Type",
    path: "/general-masters/client-type",
    type: "clientType",
  },
];

interface FetchParams {
  limit?: number;
  page?: number;
  search?: string;
}

const GeneralMastersLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<AppThunkDispatch>();

  useEffect(() => {
    setLoading(true);
    const currentPath = location.pathname;
    const tabIndex = tabs.findIndex((tab) => tab.path === currentPath);
    if (tabIndex !== -1 && tabIndex !== activeTab) {
      setActiveTab(tabIndex);
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [location]);

  const fetchMasterData = async (
    type: MasterType,
    params: FetchParams = {},
    isArchiveView: boolean = false,
  ) => {
    try {
      const { getAllData } = actionsHandler(type as EndpointType);
      const customQuery = `is_archive=${isArchiveView}`;
      const response = await dispatch(
        getAllData({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          customQuery,
        }),
      );

      return response;
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
      return {};
    } finally {
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    navigate(tabs[newValue].path);
  };

  // Keep your existing JSX until the main content section

  return (
    <div className="tw-relative">
      <HeaderComponent />
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-8 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <img
                    src={BackIcon}
                    alt="Back Icon"
                    onClick={() => navigate("/dashboard")}
                    className="tw-cursor-pointer"
                  />
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                    General Masters
                  </h1>
                </div>
              </div>
              <div className="tw-w-fit tw-mt-4 tw-border tw-border-[#E9EAEB]">
                <StyledTabs value={activeTab} onChange={handleTabChange}>
                  {tabs.map((tab, index) => (
                    <StyledTab
                      key={tab.label}
                      label={tab.label}
                      className={`tw-text-sm tw-font-semibold ${
                        activeTab === index ? "active-tab" : "tw-text-[#232323]"
                      } tab-border`}
                    />
                  ))}
                </StyledTabs>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="tw-mt-[177px]">
            {activeTab !== null && (
              <MasterDataTable
                type={tabs[activeTab].type}
                fetchData={fetchMasterData}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralMastersLayout;
